import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import MetricCard from "./MetricCard";
import DonutCard from "./DonutCard";
import BannerPlacement, { BANNER_MAX_WIDTH } from "../../../components/BannerPlacement";
import { ACCORDION_BUTTON_SX, CARD_SURFACE_SX, KPI_COLORS, dimSx } from "../../../styles/tokens";
import {
  CurrencyRuble,
  Discount,
  Percent,
  ShoppingCart,
  Group,
  AccountBalanceWallet,
  Store,
  Payment,
  Home,
  Storefront,
  LocalShipping,
  Wallet,
  CreditCard,
} from "@mui/icons-material";

// Заказы и гости — целыми: iiko делит заказ на несколько групп дробями
const wholeCount = (n) => Math.round(Number(n) || 0).toLocaleString("ru-RU");

/**
 * Контейнер карточек метрик.
 * Добавлена поддержка отображения трендов (▲▼ +%).
 */
const MetricsCardsContainer = ({ filteredData, trends, loading, token }) => {
  const [isOriginsExpanded, setIsOriginsExpanded] = useState(false);
  
  // 🔹 Проверяем тренды при каждом обновлении
  useEffect(() => {
    console.log("🔥 Тренды в карточках:", trends);
    console.log("🧮 Метрики:", filteredData);
  }, [trends, filteredData]);

  if (!filteredData && !loading) return null;

  // Данных ещё нет — скелетоны; данные есть и идёт перезагрузка — старые остаются приглушёнными
  const skeleton = !filteredData;
  const dimmed = loading && !skeleton;

  const {
    totalSum = 0,
    totalDiscount = 0,
    discountPercent = 0,
    totalOrders = 0,
    totalGuests = 0,
    avgOrder = 0,
    avgPerGuest = 0,
    hallPercent = 0,
    deliveryPercent = 0,
    cashPercent = 0,
    nonCashPercent = 0,
    orderTypeBreakdown = [],
    payTypeBreakdown = [],
    allPayTypesBreakdown = [],
    deliveryOriginsBreakdown = [],
  } = filteredData || {};

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: { xs: 1.5, md: 2 },
        width: "100%",
        maxWidth: { xs: BANNER_MAX_WIDTH, md: "100%" },
        alignItems: "stretch",
        // Приглушаем карточки, баннер — нет
        "& > :not([data-no-dim])": dimSx(dimmed),
      }}
    >
      {/* 📢 Баннер перед основной карточкой */}
      <Box data-no-dim sx={{ gridColumn: "1 / -1" }}>
        <BannerPlacement placement="home_top_promo" token={token} loading={loading} />
      </Box>

      {/* 💰 Большая карточка "Сумма со скидкой" */}
      <Box sx={{ gridColumn: "1 / -1" }}>
      <MetricCard
        icon={CurrencyRuble}
        title="Сумма со скидкой"
        valueNumber={totalSum}
        format={(n) =>
          Number(n).toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        }
        suffix=" ₽"
        trend={trends?.delta ?? null}
        trendLabel={trends?.label}
        color={KPI_COLORS.revenueMain}
        large
        loading={skeleton}
        expandable={true}
        expandedContent={(() => {
          // Цвета из доната "Типы оплат" (allPayTypesBreakdown)
          // Используем те же цвета, что и в DonutCard для allPayTypesBreakdown
          const allPayTypesColors = [
            "#26c6da", // бирюзовый
            "#ab47bc", // фиолетовый
            "#ff7043", // оранжевый
            "#42a5f5", // синий
            "#66bb6a", // зеленый
            "#ef5350", // красный
            "#ffa726", // янтарный
            "#7e57c2", // фиолетовый темный
            "#00acc1", // голубой
            "#ec407a", // розовый
            "#5c6bc0", // индиго
            "#26a69a", // бирюзовый темный
            "#ffca28", // желтый
            "#8d6e63", // коричневый
            "#78909c", // сине-серый
          ];
          
          // Создаем мапу цветов на основе оригинального порядка allPayTypesBreakdown
          // Это важно, так как после сортировки порядок изменится
          const colorMap = new Map();
          if (allPayTypesBreakdown && allPayTypesBreakdown.length > 0) {
            allPayTypesBreakdown.forEach((item, index) => {
              const label = item.label || item.name || "Не указан";
              const color = allPayTypesColors[index % allPayTypesColors.length];
              colorMap.set(label, color);
            });
          }
          
          // Также добавляем цвета для "Налич / Безнал" из соответствующего доната
          const payTypeColors = ["#00acc1", "#ec407a"]; // наличные, безнал
          payTypeBreakdown.forEach((payType, index) => {
            const color = payTypeColors[index % payTypeColors.length];
            // Приоритет: если тип оплаты есть в allPayTypesBreakdown, используем его цвет
            // Иначе используем цвет из payTypeBreakdown
            if (!colorMap.has(payType.label)) {
              colorMap.set(payType.label, color);
            }
          });
          
          return allPayTypesBreakdown
            ?.map((item) => {
              const label = item.label || item.name || "Не указан";
              
              // Используем цвет из мапы (по оригинальному порядку в allPayTypesBreakdown)
              let itemColor = colorMap.get(label);
              
              // Если цвет не найден, проверяем по содержимому названия
              if (!itemColor) {
                const labelLower = label.toLowerCase();
                if (labelLower.includes("налич")) {
                  itemColor = "#00acc1"; // цвет наличных из доната "Налич / Безнал"
                } else if (labelLower.includes("безнал")) {
                  itemColor = "#ec407a"; // цвет безнала из доната "Налич / Безнал"
                } else {
                  // Fallback: используем первый цвет из палитры
                  itemColor = allPayTypesColors[0];
                }
              }
              
              return {
                label,
                // Для "(без оплаты)" показываем скидку, для остальных - сумму продаж
                value: label === "(без оплаты)" || label?.includes("без оплаты")
                  ? (item.discount || 0)
                  : (item.value || item.sum || 0),
                color: itemColor, // Цвет соответствует донату "Типы оплат"
                ordersCount: item.ordersCount || 0, // Количество заказов
                percent: item.percent ?? null,
              };
            })
            .sort((a, b) => (b.value || 0) - (a.value || 0)) || [];
        })()}
      />
      </Box>

      {/* 🧾 Все типы оплат (Donut) */}
      <Box sx={{ gridColumn: { xs: "1 / -1", md: "1" }, gridRow: { md: 4 }, height: "100%", minWidth: 0 }}>
        {(() => {
          // Сортируем все типы оплат по значению (от большего к меньшему)
          const sortedPayTypes = [...(allPayTypesBreakdown || [])].sort(
            (a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)
          );
          // Остальные типы оплат (начиная с 5-го, так как топ-4 уже в легенде)
          const remainingPayTypes = sortedPayTypes.slice(4);
          
          return (
            <DonutCard
              loading={skeleton}
              title="Типы оплат"
              data={allPayTypesBreakdown}
              colors={[
                "#26c6da", // бирюзовый
                "#ab47bc", // фиолетовый
                "#ff7043", // оранжевый
                "#42a5f5", // синий
                "#66bb6a", // зеленый
                "#ef5350", // красный
                "#ffa726", // янтарный
                "#7e57c2", // фиолетовый темный
                "#00acc1", // голубой
                "#ec407a", // розовый
                "#5c6bc0", // индиго
                "#26a69a", // бирюзовый темный
                "#ffca28", // желтый
                "#8d6e63", // коричневый
                "#78909c", // сине-серый
              ]}
              maxLegendItems={4}
              expandableContent={remainingPayTypes.length > 0 ? remainingPayTypes : null}
            />
          );
        })()}
      </Box>

      <Box
        sx={{
          gridColumn: "1 / -1",
          gridRow: { md: 3 },
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
          gap: { xs: 1.5, md: 2 },
          alignItems: "stretch",
        }}
      >
      {/* 💸 Сумма скидки */}
      <Box sx={{ height: "100%", minWidth: 0 }}>
      <MetricCard
        icon={Discount}
        title="Сумма скидки"
        valueNumber={totalDiscount}
        format={(n) =>
          // Целые рубли, как себестоимость и прибыль: с копейками на телефоне сумма не помещается
          Number(n).toLocaleString("ru-RU", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        }
        suffix=" ₽"
        color={KPI_COLORS.discountSum}
        loading={skeleton}
      />
      </Box>

      {/* 📉 Процент скидки */}
      <Box sx={{ height: "100%", minWidth: 0 }}>
      <MetricCard
        icon={Percent}
        title="Процент скидки"
        valueNumber={(function () {
          const rawVal = Number(discountPercent) || 0;
          return rawVal > 100 ? rawVal / 100 : rawVal;
        })()}
        format={(n) =>
          Number(n).toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        }
        suffix=" %"
        color={KPI_COLORS.discountPercent}
        loading={skeleton}
      />
      </Box>

      {/* 🛒 Кол-во заказов. iiko делит заказ на несколько групп дробями (208,333…) — показываем целые.
          Не через value: MetricCard вычищает из строки всё, кроме цифр, и вместе с точкой (208 333 333 333) */}
      <Box sx={{ height: "100%", minWidth: 0 }}>
      <MetricCard
        icon={ShoppingCart}
        title="Кол-во заказов"
        valueNumber={totalOrders}
        format={wholeCount}
        color={KPI_COLORS.ordersCount}
        loading={skeleton}
      />
      </Box>

      {/* 👥 Кол-во гостей */}
      <Box sx={{ height: "100%", minWidth: 0 }}>
      <MetricCard
        icon={Group}
        title="Кол-во гостей"
        valueNumber={totalGuests}
        format={wholeCount}
        color={KPI_COLORS.guestsCount}
        loading={skeleton}
      />
      </Box>

      {/* 💳 Средний чек */}
      <Box sx={{ height: "100%", minWidth: 0 }}>
      <MetricCard
        icon={AccountBalanceWallet}
        title="Средний чек"
        valueNumber={avgOrder}
        format={(n) =>
          Number(n).toLocaleString("ru-RU", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })
        }
        suffix=" ₽"
        color={KPI_COLORS.averageCheck}
        loading={skeleton}
      />
      </Box>

      {/* 👤 Средняя выручка с гостя */}
      <Box sx={{ height: "100%", minWidth: 0 }}>
      <MetricCard
        icon={CurrencyRuble}
        title="Ср. выручка с гостя"
        valueNumber={avgPerGuest}
        format={(n) =>
          Number(n).toLocaleString("ru-RU", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })
        }
        suffix=" ₽"
        color={KPI_COLORS.revenuePerGuest}
        loading={skeleton}
      />
      </Box>
      </Box>

      {/* 🏠 Объединенная карточка: Зал/Доставка и Налич/Безнал */}
      <Box sx={{ gridColumn: { xs: "1 / -1", md: "2" }, gridRow: { md: 4 }, height: "100%", minWidth: 0 }}>
        <Box
          sx={{
            ...CARD_SURFACE_SX,
            p: 2,
            display: "flex",
            flexDirection: "column",
            // Содержимое по центру, когда соседняя карточка «Типы оплат» выше
            justifyContent: "center",
            gap: 2,
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Донаты и легенды горизонтально */}
          <Box sx={{ display: "flex", justifyContent: "space-around", alignItems: "flex-start", gap: 3, flexWrap: "wrap" }}>
            {/* Донат Зал / Доставка */}
            <DonutCard
              loading={skeleton}
              title="Зал / Доставка"
              data={orderTypeBreakdown}
              colors={["#7e57c2", "#ff7043"]}
              showLegendBelow={true}
              noBackground={true}
            />
            
            {/* Донат Налич / Безнал */}
            <DonutCard
              loading={skeleton}
              title="Налич / Безнал"
              data={payTypeBreakdown}
              colors={["#00acc1", "#ec407a"]}
              showLegendBelow={true}
              noBackground={true}
            />
          </Box>

          {/* Раскрывающаяся секция с источниками заказа доставки - под легендами */}
          {deliveryOriginsBreakdown && deliveryOriginsBreakdown.length > 0 && (
            <>
              <Box sx={{ pt: 1, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <ButtonBase
                onClick={() => setIsOriginsExpanded(!isOriginsExpanded)}
                aria-expanded={isOriginsExpanded}
                sx={ACCORDION_BUTTON_SX}
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "rgba(255,255,255,0.9)",
                    fontWeight: 500,
                  }}
                >
                  Источники заказа доставки
                </Typography>
                <KeyboardArrowDown
                  sx={{
                    fontSize: 18,
                    color: "rgba(255,255,255,0.9)",
                    transform: isOriginsExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                />
              </ButtonBase>
              </Box>
              <Box
                sx={{
                  maxHeight: isOriginsExpanded ? "1000px" : "0px",
                  opacity: isOriginsExpanded ? 1 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out",
                  mt: isOriginsExpanded ? 1.5 : 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {deliveryOriginsBreakdown.map((item, index) => {
                  const itemColor = ["#7e57c2", "#ff7043"][index % 2] || `hsl(${(index * 70) % 360} 70% 55%)`;
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.4,
                        px: 1,
                        py: 0.8,
                        borderRadius: 1,
                        background: `${itemColor}15`,
                        border: `1px solid ${itemColor}40`,
                        borderLeft: `4px solid ${itemColor}`,
                        opacity: isOriginsExpanded ? 1 : 0,
                        transform: isOriginsExpanded ? "translateY(0)" : "translateY(-10px)",
                        transition: `opacity 0.3s ease ${index * 0.03}s, transform 0.3s ease ${index * 0.03}s`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.9)", fontWeight: 500 }}>
                          {item.label}:
                        </Typography>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#fff" }}>
                          {item.ordersCount !== undefined && item.ordersCount > 0 
                            ? `${item.ordersCount} ${item.ordersCount === 1 ? "заказ" : item.ordersCount < 5 ? "заказа" : "заказов"}`
                            : "0 заказов"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MetricsCardsContainer;
